import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useClientStore } from '../stores/clientStore';
import { clientsApi } from '../api/clients';
import { CreateClientModal } from '../components/clients/CreateClientModal';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { formatRelativeTime } from '../utils/dateFormat';
import clsx from 'clsx';

// Animation variants - defined outside component to prevent recreation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.02 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.15 } },
};

function getActivityStatus(lastActivity: string | null | undefined) {
  if (!lastActivity) return { color: 'gray' };
  const date = new Date(lastActivity);
  const now = new Date();
  const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffHours < 24) return { color: 'green' };
  if (diffHours < 72) return { color: 'yellow' };
  return { color: 'gray' };
}

export function ClientsPage() {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { clients, isLoading, searchQuery, setClients, setIsLoading, setSearchQuery } =
    useClientStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const response = await clientsApi.list();
      setClients(response.clients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="w-5 h-5" />
          {t('newClient')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filteredClients.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <UserGroupIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {searchQuery ? t('empty.searchTitle') : t('empty.title')}
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm mb-6">
            {searchQuery
              ? t('empty.searchDescription')
              : t('empty.description')}
          </p>
          {!searchQuery && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="w-5 h-5" />
              {t('empty.createFirst')}
            </Button>
          )}
        </motion.div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-1"
        >
          {filteredClients.map((client) => {
            const activityStatus = getActivityStatus(client.lastActivity);
            return (
              <motion.div
                key={client.id}
                variants={itemVariants}
                onClick={() => navigate(`/clients/${client.id}`)}
                className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BuildingOfficeIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{client.name}</p>
                    <div
                      className={clsx(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        activityStatus.color === 'green' && 'bg-green-500',
                        activityStatus.color === 'yellow' && 'bg-yellow-500',
                        activityStatus.color === 'gray' && 'bg-gray-300 dark:bg-slate-600'
                      )}
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {client.company && `${client.company} • `}
                    {t('card.filesCount', { count: client.fileCount ?? 0 })} • {formatRelativeTime(client.lastActivity)}
                  </p>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Create Modal */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={(client) => {
          setIsCreateModalOpen(false);
          navigate(`/clients/${client.id}`);
        }}
      />
    </div>
  );
}
